import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import SearchableSelect from '../../components/SearchableSelect';
import ProveedorSearch from '../../components/ProveedorSearch';
import { labelUnidadMedida } from '../../utils/unidadMedida';

export interface ProveedorCostoLinea {
  id: string;
  codigoProveedor: string;
  nombreProveedor?: string;
  codigoArticuloProveedor: string;
  costo: number | '';
  umCompra: string;
}

interface UnidadOption {
  idUnidadMedida: string;
  descripcion: string;
}

interface Props {
  lineas: ProveedorCostoLinea[];
  unidadesMedida: UnidadOption[];
  umsDisponibles?: string[];
  onChange: (lineas: ProveedorCostoLinea[]) => void;
  disabled?: boolean;
}

const newId = () => `cpp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const createEmptyProveedorCosto = (): ProveedorCostoLinea => ({
  id: newId(),
  codigoProveedor: '',
  nombreProveedor: '',
  codigoArticuloProveedor: '',
  costo: '',
  umCompra: '',
});

const ProductoProveedoresCostoSection: React.FC<Props> = ({
  lineas,
  unidadesMedida,
  umsDisponibles,
  onChange,
  disabled,
}) => {
  const [searchOpenFor, setSearchOpenFor] = useState<string | null>(null);

  const umOptions = (unidadesMedida || [])
    .filter((u) => {
      if (!umsDisponibles || umsDisponibles.length === 0) return true;
      return umsDisponibles.some((x) => x.toUpperCase() === u.idUnidadMedida.trim().toUpperCase());
    })
    .map((u) => ({
      value: u.idUnidadMedida,
      label: labelUnidadMedida(u),
    }));

  const update = (id: string, patch: Partial<ProveedorCostoLinea>) => {
    onChange(lineas.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Proveedores / UM de compra
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Definí la UM de compra por defecto por proveedor. Debe ser la UM base o una equivalencia definida.
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Proveedor</TableCell>
              <TableCell width={140}>Cód. art. proveedor</TableCell>
              <TableCell width={110}>Costo</TableCell>
              <TableCell width={280}>UM compra</TableCell>
              <TableCell width={56} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lineas.map((l) => (
              <TableRow key={l.id}>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={
                      l.codigoProveedor
                        ? `${l.codigoProveedor}${l.nombreProveedor ? ` – ${l.nombreProveedor}` : ''}`
                        : ''
                    }
                    placeholder="Buscar proveedor"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <IconButton
                          size="small"
                          disabled={disabled}
                          onClick={() => setSearchOpenFor(l.id)}
                        >
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      ),
                    }}
                  />
                </TableCell>
                <TableCell sx={{ width: 140, maxWidth: 140 }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={l.codigoArticuloProveedor}
                    disabled={disabled}
                    onChange={(e) => update(l.id, { codigoArticuloProveedor: e.target.value })}
                  />
                </TableCell>
                <TableCell sx={{ width: 110 }}>
                  <TextField
                    type="number"
                    size="small"
                    fullWidth
                    value={l.costo}
                    disabled={disabled}
                    onChange={(e) =>
                      update(l.id, { costo: e.target.value === '' ? '' : Number(e.target.value) })
                    }
                    inputProps={{ min: 0, step: 'any' }}
                  />
                </TableCell>
                <TableCell sx={{ width: 280, minWidth: 240 }}>
                  <SearchableSelect
                    name={`umCompra-${l.id}`}
                    label=""
                    allowEmpty
                    emptyOptionLabel="UM base"
                    value={l.umCompra || null}
                    onChange={(v) => update(l.id, { umCompra: v ?? '' })}
                    options={umOptions}
                    margin="none"
                    disabled={disabled}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={disabled}
                    onClick={() => onChange(lineas.filter((x) => x.id !== l.id))}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {lineas.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    Sin proveedores asociados.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          disabled={disabled}
          onClick={() => onChange([...lineas, createEmptyProveedorCosto()])}
        >
          Agregar proveedor
        </Button>
      </Box>

      <ProveedorSearch
        open={Boolean(searchOpenFor)}
        onClose={() => setSearchOpenFor(null)}
        onSelect={(prov) => {
          if (!searchOpenFor) return;
          update(searchOpenFor, {
            codigoProveedor: String(prov.nroProveedor),
            nombreProveedor: prov.nombre || '',
          });
          setSearchOpenFor(null);
        }}
      />
    </Stack>
  );
};

export default ProductoProveedoresCostoSection;
